<template>
  <div class="login-page">
    <!-- Background -->
    <div class="login-background">
      <div class="grid-overlay"></div>
    </div>

    <!-- Login Container -->
    <div class="login-container">
      <!-- Logo & Branding -->
      <div class="login-header">
        <svg class="logo-svg" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
            </linearGradient>
          </defs>
          <path d="M 15 10 L 25 10 L 35 25 L 25 40 L 15 40 L 25 25 Z" fill="url(#logoGradient)"/>
          <circle cx="40" cy="15" r="3" fill="url(#logoGradient)"/>
          <circle cx="40" cy="35" r="3" fill="url(#logoGradient)"/>
        </svg>
        <div class="logo-text">
          <span class="logo-name">Nick Volkmann</span>
          <span class="logo-subtitle">Client Portal</span>
        </div>
      </div>

      <!-- Login Card -->
      <div class="login-card">
        <div class="card-header">
          <h1>Willkommen zurück</h1>
          <p>Melden Sie sich an, um auf Ihre Projekte zuzugreifen</p>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{{ error }}</span>
        </div>

        <!-- Login Form -->
        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="username">Benutzername</label>
            <input
              id="username"
              v-model="credentials.username"
              type="text"
              required
              placeholder="Ihr Benutzername"
              class="form-input"
              autocomplete="username"
            />
          </div>

          <div class="form-group">
            <label for="password">Passwort</label>
            <input
              id="password"
              v-model="credentials.password"
              type="password"
              required
              placeholder="Ihr Passwort"
              class="form-input"
              autocomplete="current-password"
            />
          </div>

          <button type="submit" class="submit-button" :disabled="loading">
            <span v-if="loading">Wird angemeldet...</span>
            <span v-else>Anmelden</span>
            <svg v-if="!loading" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <button @click="goBack" class="back-btn" type="button" aria-label="Zurück zur Website">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
            <path d="M19 12H5"/>
            <path d="m12 19-7-7 7-7"/>
          </svg>
          <span>Zurück</span>
          </button>
        </form>

        <!-- Footer Info -->
        <div class="login-footer">
          <p>Bei Problemen mit Ihrem Zugang kontaktieren Sie bitte Ihren Ansprechpartner</p>
        </div>
      </div>

      <!-- Trust Badges -->
      <div class="trust-section">
        <div class="trust-badge">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span>Sichere Verbindung</span>
        </div>
        <div class="trust-badge">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span>DSGVO-konform</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { login } from '@/apiService' // 👈 zentraler Service

const router = useRouter()

const credentials = ref({
  username: '',
  password: ''
})

const loading = ref(false)
const error = ref('')

const handleLogin = async () => {
  loading.value = true
  error.value = ''

  try {
    const data = await login(credentials.value)

    // ✅ SimpleJWT: Erfolg, wenn "access" da ist
    if (data?.access) {
      router.push('/portfolio')
    } else {
      error.value = data?.message || 'Anmeldung fehlgeschlagen.'
    }
  } catch (err) {
    console.error("Login failed:", err?.response?.status, err?.response?.data || err.message)
    error.value =
      err?.response?.data?.detail ||
      (err?.response?.status === 404
        ? 'Login-Endpoint nicht gefunden (404). Prüfe Backend-Route.'
        : 'Anmeldung fehlgeschlagen. Bitte Zugangsdaten prüfen.')
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/')
}
</script>

<style scoped>
/* dein bestehendes Styling – unverändert */
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
}

.login-background {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: 0;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.login-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 480px;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
}

.logo-svg {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.logo-text {
  display: flex;
  flex-direction: column;
  color: white;
}

.logo-name {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.logo-subtitle {
  font-size: 14px;
  opacity: 0.9;
  font-weight: 500;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  width: 100%;
  padding: 14px 24px;

  /* dezente Secondary-Optik */
  background: rgba(102, 126, 234, 0.06);            /* leichter Glas-Effekt */
  border: 2px solid rgba(102, 126, 234, 0.25);      /* Outline in Brand-Farbe */
  color: #4a5568;
  border-radius: 12px;

  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease,
              background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  margin-top: 8px; /* kleiner Abstand zur Submit-Button */
}

.back-btn svg {
  flex-shrink: 0;
  transform: translateX(0);
  transition: transform 0.2s ease;
}

.back-btn:hover {
  transform: translateY(-1px);
  background: rgba(102, 126, 234, 0.12);
  border-color: rgba(102, 126, 234, 0.45);
  color: #1a202c;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
}

.back-btn:hover svg {
  transform: translateX(-2px); /* kleines „Zurück“-Motion */
}

.back-btn:active {
  transform: translateY(0);
  box-shadow: none;
}

.back-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.back-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.login-card {
  background: white;
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.card-header {
  margin-bottom: 32px;
}

.card-header h1 {
  font-size: 28px;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 8px;
}

.card-header p {
  font-size: 15px;
  color: #4a5568;
  line-height: 1.6;
}

.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert-error {
  background: #fee2e2;
  color: #991b1b;
  border-left: 4px solid #dc2626;
}

.alert svg {
  flex-shrink: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
}

.form-input {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.3s;
  background: white;
  color: #1a202c;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.form-input::placeholder {
  color: #a0aec0;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 8px;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.4);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-button svg {
  flex-shrink: 0;
}

.login-footer {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}

.login-footer p {
  font-size: 13px;
  color: #718096;
  line-height: 1.6;
}

.trust-section {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.trust-badge {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 13px;
  font-weight: 600;
}

.trust-badge svg {
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .login-card {
    padding: 32px 24px;
  }

  .card-header h1 {
    font-size: 24px;
  }

  .login-header {
    padding: 20px;
  }

  .trust-section {
    flex-direction: column;
  }
}
</style>
